import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import { Input } from '@/components/ui/input';
import { searchStories } from '@/services/storyService';
import { listPublicStories } from '@/services/storyRepo';
import SearchFilters from '@/components/SearchFilters';
import HeroSearchTypeahead from '@/components/HeroSearchTypeahead';
import StoryListCard from '@/components/StoryListCard';
import { useTagOptions } from '@/hooks/useTagOptions';
import { useHeroTypeahead } from '@/hooks/useHeroTypeahead';
import { inferImpliedTagIds } from '@/lib/tagSearch';
import {
  FILTER_DIMENSIONS,
  paramsToSet,
  setToParam,
  selectedFromParams,
  selectedToFiltersPayload,
  totalSelectedCount,
} from '@/lib/filters';
import { SEARCH_SUGGESTIONS, SEARCH_PLACEHOLDER } from '@/lib/searchSuggestions';
import { analyticsNumber, trackEvent } from '@/lib/analytics';
import Seo from '@/components/Seo';

const SEARCH_MESSAGES = [
  'Listening to your words...',
  'Sensing the feeling behind them...',
  'Walking through other journeys...',
  'Bringing back stories that resonate...',
];

const normalizeQuery = (value = '') => value.trim().toLowerCase();

const flattenFilterProps = (filters) => {
  const keys = Object.keys(filters || {}).sort();
  const values = keys.flatMap((k) => (Array.isArray(filters[k]) ? [...filters[k]] : []));
  return {
    filter_keys: keys,
    filter_values_flat: values,
  };
};

/** Must match the fetch cap in `performSearch` (hybrid search max; list page size). */
const SEARCH_FETCH_LIMIT = 20;

const SearchResultsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  // ?filters=open from the landing-page "More filters" link auto-expands
  // the panel on arrival so the user lands on something immediately useful
  // instead of a near-empty page with one more click required.
  const filtersOpenIntent = searchParams.get('filters') === 'open';

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(query);
  const [loadingMessageIdx, setLoadingMessageIdx] = useState(0);
  /** Exact total for tag-only browse (Supabase count); row count for text search (floor at limit). */
  const [matchCount, setMatchCount] = useState(null);
  const [matchCountIsFloor, setMatchCountIsFloor] = useState(false);

  const { tagOptions } = useTagOptions();

  // Monotonic request id — stale responses (from older queries) are dropped
  // instead of clobbering fresh results. No AbortController on invoke, so
  // this is the simplest "last write wins" pattern.
  const reqIdRef = useRef(0);

  // URL is the source of truth for both query and filters.
  const selected = useMemo(() => selectedFromParams(searchParams), [searchParams]);
  const totalSelected = totalSelectedCount(selected);

  const filterOrQueryActive = !!query.trim() || totalSelected > 0;
  const matchSummary = useMemo(() => {
    if (!filterOrQueryActive) return undefined;
    if (loading) return 'Searching…';
    if (matchCount === null) return undefined;
    if (matchCount === 0) return 'No matches';
    if (matchCountIsFloor) return `${matchCount}+ stories`;
    return `${matchCount} ${matchCount === 1 ? 'story' : 'stories'}`;
  }, [filterOrQueryActive, loading, matchCount, matchCountIsFloor]);

  // Keep the input in sync when the URL `q` changes (back/forward nav, or
  // when handleSearch clears it). Without this, clearing the input and
  // hitting Search would leave the field looking blank but the URL holding
  // the old query on subsequent filter toggles. (It already wouldn't, since
  // we re-read `query` from URL — but this keeps the visual state honest
  // when arriving via browser navigation.)
  useEffect(() => {
    setSearchQuery(query);
  }, [query]);

  useEffect(() => {
    if (!loading) {
      setLoadingMessageIdx(0);
      return undefined;
    }
    const timer = setInterval(() => {
      setLoadingMessageIdx((prev) => (prev + 1) % SEARCH_MESSAGES.length);
    }, 1200);
    return () => clearInterval(timer);
  }, [loading]);

  const performSearch = useCallback(async () => {
    const filters = selectedToFiltersPayload(selected);
    const normalizedQuery = normalizeQuery(query);
    const { filter_keys, filter_values_flat } = flattenFilterProps(filters);
    const filterCount = Object.values(filters).reduce(
      (sum, values) => sum + (Array.isArray(values) ? values.length : 0),
      0
    );
    // Routing contract:
    //   • Edge function (km_search_stories): ONLY when the URL carries
    //     fresh=1 (set by an explicit submit handler) AND the query is
    //     long enough for the edge function's INVALID_ARGUMENT guard
    //     (>= 3 chars, see backend-v2/functions/main.py).
    //   • PostgREST (listPublicStories): every reactive update — chip
    //     toggle, filter clear, page load without a submit marker.
    //     Filter-only; the typed query is informational until the user
    //     submits again.
    //   • Nothing at all: no filters AND no submit marker → empty.
    // This keeps the expensive embedding call gated behind the user's
    // explicit "search" intent. Cheap PostgREST handles narrowing.
    const isExplicitSubmit = searchParams.get('fresh') === '1';
    const hasQuery = query.trim().length >= 3;
    const hasFilters = Object.keys(filters).length > 0;
    const useEdgeFunction = isExplicitSubmit && hasQuery;

    if (!useEdgeFunction && !hasFilters) {
      setResults([]);
      setMatchCount(null);
      setMatchCountIsFloor(false);
      return;
    }

    const reqId = ++reqIdRef.current;
    const startedAt = performance.now();
    setLoading(true);
    setMatchCount(null);
    setMatchCountIsFloor(false);
    try {
      let stories;
      let serverTiming = null;
      if (useEdgeFunction) {
        // Query-side tag expansion (soft lane). Reuse the typeahead matcher
        // to infer tag slugs from the free text — an alias the user typed
        // but didn't click ("livelihood" → `career`) still widens recall.
        // Excludes already-selected chips (those ride the hard filters).
        // Never filters; the RPC folds these in as an extra FTS match path.
        const impliedTags = inferImpliedTagIds(query, tagOptions, { selectedById: selected });
        const out = await searchStories(query, { limit: SEARCH_FETCH_LIMIT, filters, impliedTags });
        if (reqId !== reqIdRef.current) return;
        stories = out.stories;
        serverTiming = out.debug_timing_ms;
        setResults(stories);
        setMatchCount(stories.length);
        setMatchCountIsFloor(stories.length >= SEARCH_FETCH_LIMIT);
      } else {
        // Reactive (PostgREST) path: filter-only by design — chip toggles
        // and ambient URL changes shouldn't pay the embedding cost. To
        // bring the typed query back into the search, the user submits.
        const listed = await listPublicStories({ limit: SEARCH_FETCH_LIMIT, ...filters });
        if (reqId !== reqIdRef.current) return;
        stories = listed.stories;
        setResults(stories);
        setMatchCount(listed.total ?? stories.length);
        setMatchCountIsFloor(false);
      }
      const latencyMs = Math.round(performance.now() - startedAt);
      const embedMs = analyticsNumber(serverTiming?.embed_ms);
      const rpcMs = analyticsNumber(serverTiming?.rpc_ms);
      const serverTotalMs = analyticsNumber(serverTiming?.total_ms);
      trackEvent('search_executed', {
        query: query.trim() || null,
        query_normalized: normalizedQuery || null,
        query_length: query.trim().length,
        has_query: hasQuery,
        has_filters: hasFilters,
        filters,
        filter_keys,
        filter_values_flat,
        filter_count: filterCount,
        result_count: stories.length,
        /** Browser wall-clock (ms); integer for PostHog numeric filters (e.g. > 500). */
        latency_ms: latencyMs,
        /** Server-side split from km-search-stories; omitted on tag-only browse. */
        ...(embedMs !== undefined ? { embed_ms: embedMs } : {}),
        ...(rpcMs !== undefined ? { rpc_ms: rpcMs } : {}),
        ...(serverTotalMs !== undefined ? { server_total_ms: serverTotalMs } : {}),
        source: 'search_results_page',
      });
    } catch (error) {
      if (reqId !== reqIdRef.current) return;
      console.error('Error searching:', error);
      setResults([]);
      setMatchCount(null);
      setMatchCountIsFloor(false);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
    // `searchParams` is included so the `fresh=1` marker change retriggers
    // performSearch even when query + selected (derived from the same URL)
    // happen to be reference-equal. `tagOptions` is read for query-side tag
    // inference (implied tags) — re-run once the tag catalog has loaded.
  }, [query, selected, searchParams, tagOptions]);

  useEffect(() => { performSearch(); }, [performSearch]);

  // Submitting the form commits the input value to the URL — including
  // when it's empty (clears `q` so subsequent filter toggles don't run with
  // a stale query).
  // Form-submit handler — explicit search intent. Sets fresh=1 so the
  // edge function fires once with the typed query + active filters.
  // The chip-toggle handler below strips fresh, so subsequent narrowing
  // stays on the PostgREST path.
  const handleSearch = (e) => {
    e.preventDefault();
    closeNow();
    const next = new URLSearchParams(searchParams);
    const trimmed = searchQuery.trim();
    if (trimmed) next.set('q', trimmed);
    else next.delete('q');
    next.set('fresh', '1');
    setSearchParams(next);
  };

  // Filter callbacks for <SearchFilters />. Both write straight to the URL
  // since the URL is our single source of truth for filter state. They
  // also strip the `fresh` marker — chip toggles are reactive and must
  // route through PostgREST (filter-only), not the edge function.
  const handleToggleTag = (dimensionKey, tagId) => {
    const param = FILTER_DIMENSIONS.find((d) => d.key === dimensionKey)?.param;
    if (!param) return;
    const next = new URLSearchParams(searchParams);
    const current = paramsToSet(next.get(param));
    if (current.has(tagId)) current.delete(tagId);
    else current.add(tagId);
    if (current.size) next.set(param, setToParam(current));
    else next.delete(param);
    next.delete('fresh');
    setSearchParams(next);
  };

  // "Clear all" is the user's reset affordance — it wipes the typed
  // query alongside the filter chips so the input, URL `q`, and filter
  // params can't drift apart (previously left a stale `q` behind that
  // showed up as "No stories found for …" after picking new tags).
  const handleClearFilters = () => {
    const next = new URLSearchParams(searchParams);
    FILTER_DIMENSIONS.forEach(({ param }) => next.delete(param));
    next.delete('q');
    next.delete('fresh');
    setSearchQuery('');
    setSearchParams(next);
  };

  // X inside the search input — clears just the typed query (input +
  // URL `q`), leaves filter chips intact for granular cleanup.
  const handleClearQuery = () => {
    setSearchQuery('');
    const next = new URLSearchParams(searchParams);
    next.delete('q');
    next.delete('fresh');
    setSearchParams(next);
    inputRef.current?.focus();
  };

  const { inputRef, isOpen, inputProps, onTypeaheadPick, closeNow } =
    useHeroTypeahead({ onPick: handleToggleTag });

  const handleResultClick = useCallback((story, rank) => {
    const filters = selectedToFiltersPayload(selected);
    const normalizedQuery = normalizeQuery(query);
    const { filter_keys, filter_values_flat } = flattenFilterProps(filters);
    trackEvent('search_result_clicked', {
      query: query.trim() || null,
      query_normalized: normalizedQuery || null,
      rank,
      story_id: story.id,
      story_title: story.title,
      has_semantic_score: (story.similarity_score || 0) > 0,
      similarity_score: story.similarity_score ?? null,
      rrf_score: story.rrf_score ?? null,
      filters,
      filter_keys,
      filter_values_flat,
      source: 'search_results_page',
    });
    navigate(`/story/${story.id}`);
  }, [navigate, query, selected]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* noindex: query/facet permutations are effectively infinite thin-content
          pages. Discovery flows through home, /browse, and story pages instead. */}
      <Seo
        title="Search stories · Katha Mithra"
        description="Search Katha Mithra by feeling, life area, obstacle, or the inner shift a story brought."
        path="/search"
        noindex
      />
      <PageHeader>
        <ToolbarCaptionButton
          label="Back to Home"
          shortLabel="Home"
          onClick={() => navigate('/')}
          data-testid="back-home-btn"
        />
      </PageHeader>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-5xl mx-auto mb-8">
          <SearchFilters
            selected={selected}
            tagOptions={tagOptions}
            onToggleTag={handleToggleTag}
            onClearAll={handleClearFilters}
            defaultOpen={filtersOpenIntent}
            panelCompact
            matchSummary={matchSummary}
            searchForm={
              <>
                <form onSubmit={handleSearch} className="w-full relative" data-testid="search-form">
                  <div className="bg-white rounded-2xl sm:rounded-full shadow-lg border border-[#E6E2D6] p-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-0 sm:space-x-2">
                    <div className="flex flex-1 min-w-0 items-center gap-2 pl-1 sm:pl-0">
                      <Search className="h-5 w-5 text-[#6B665E] ml-2 sm:ml-4 shrink-0" aria-hidden />
                      <Input
                        ref={inputRef}
                        type="text"
                        placeholder={SEARCH_PLACEHOLDER}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1 border-0 focus:ring-0 bg-transparent text-[#2D2A26] placeholder:text-[#6B665E] min-w-0"
                        data-testid="search-input"
                        autoComplete="off"
                        {...inputProps}
                      />
                      {searchQuery ? (
                        <button
                          type="button"
                          onClick={handleClearQuery}
                          aria-label="Clear search"
                          className="shrink-0 p-1 mr-1 rounded-full text-[#6B665E] hover:text-[#C05621] hover:bg-[#FBF4EB]"
                          data-testid="search-input-clear"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                    <Button
                      type="submit"
                      className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-4 sm:py-5 md:py-6 text-base sm:text-lg font-medium bg-[#C05621] hover:bg-[#A0461A] text-white shadow-lg hover:shadow-xl shrink-0"
                      data-testid="search-btn"
                    >
                      Search
                    </Button>
                  </div>
                  <HeroSearchTypeahead
                    query={searchQuery}
                    tagOptions={tagOptions}
                    selected={selected}
                    onToggleTag={onTypeaheadPick}
                    open={isOpen}
                  />
                </form>
                {!query.trim() && totalSelected === 0 ? (
                  <div className="text-center pt-1" data-testid="empty-suggestions">
                    <p className="text-base text-[#6B665E] mb-4">
                      Type your situation above, or start with one of these:
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {SEARCH_SUGGESTIONS.map((q) => (
                        <button
                          key={q}
                          type="button"
                          onClick={() => {
                            setSearchQuery(q);
                            const next = new URLSearchParams(searchParams);
                            next.set('q', q);
                            setSearchParams(next);
                          }}
                          className="px-3.5 py-1.5 rounded-full bg-white border border-[#E6E2D6] text-[#2D2A26] hover:border-[#C05621] hover:bg-[#F5EFE6] hover:text-[#C05621] text-sm transition-colors"
                          data-testid={`empty-suggestion-${q.replace(/\s+/g, '-')}`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            }
          />
        </div>

        {loading ? (
          <>
            <p
              className="text-center text-[#6B665E] mb-6 transition-opacity duration-300"
              data-testid="search-loading-message"
            >
              {SEARCH_MESSAGES[loadingMessageIdx]}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-loading">
              {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-[#E6E2D6] p-6 animate-pulse">
                <div className="h-3 w-20 bg-[#E6E2D6] rounded mb-3" />
                <div className="h-6 w-4/5 bg-[#E6E2D6] rounded mb-2" />
                <div className="h-6 w-3/5 bg-[#E6E2D6] rounded mb-4" />
                <div className="h-3 w-24 bg-[#E6E2D6] rounded mb-4" />
                <div className="space-y-2">
                  <div className="h-3 w-full bg-[#E6E2D6] rounded" />
                  <div className="h-3 w-5/6 bg-[#E6E2D6] rounded" />
                  <div className="h-3 w-4/6 bg-[#E6E2D6] rounded" />
                </div>
              </div>
              ))}
            </div>
          </>
        ) : results.length > 0 ? (
          <div className="mt-2 pt-8 border-t border-[#E6E2D6]/70">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3 mb-6">
              <div className="min-w-0">
                <h2 className="text-2xl sm:text-3xl font-serif text-[#2D2A26] leading-tight">
                  {query.trim()
                    ? 'Stories that may meet you here'
                    : 'Stories shaped to your filters'}
                </h2>
                <p className="text-sm text-[#6B665E] mt-1">
                  {results.length} {results.length === 1 ? 'story' : 'stories'}
                </p>
              </div>
              <span className="text-sm text-[#6B665E] px-4 py-2 bg-[#F5EFE6] rounded-full max-w-full min-w-0 text-center sm:text-left leading-snug">
                {query.trim() ? 'Matched by meaning' : 'Latest first'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-results">
              {results.map((story, idx) => {
                // Hybrid rank tier (driven by RRF order from the RPC). User-
                // facing labels frame the match as resonance, not similarity;
                // raw cosine is kept off-screen — it's a debug signal, not
                // something to show readers.
                const hasSemantic = story.similarity_score > 0;
                let badgeLabel = null;
                let badgeClass = 'bg-[#E6E2D6] text-[#6B665E]';
                if (hasSemantic) {
                  if (idx === 0) {
                    badgeLabel = 'Closest resonance';
                    badgeClass = 'bg-[#C05621] text-white';
                  } else if (idx <= 2) {
                    badgeLabel = 'Strong resonance';
                    badgeClass = 'bg-[#C05621]/15 text-[#C05621]';
                  } else {
                    badgeLabel = 'Related';
                    badgeClass = 'bg-[#C05621]/10 text-[#C05621]';
                  }
                } else if (story.rrf_score > 0) {
                  badgeLabel = 'Related';
                }
                return (
                  <StoryListCard
                    key={story.id}
                    story={story}
                    tagOptions={tagOptions}
                    onClick={() => handleResultClick(story, idx + 1)}
                    badge={badgeLabel ? { label: badgeLabel, className: badgeClass } : null}
                  />
                );
              })}
            </div>
          </div>
        ) : query.trim() || totalSelected > 0 ? (
          <div className="text-center py-12">
            <p className="text-xl text-[#6B665E] break-words px-2">
              {query.trim()
                ? (
                  <>
                    No stories found for {'\u201c'}
                    <span className="font-medium text-[#2D2A26]">{query}</span>
                    {'\u201d'}
                  </>
                )
                : 'No stories match these filters'}
            </p>
            <p className="text-sm text-[#6B665E] mt-2">
              {totalSelected > 0
                ? 'Try clearing some filters or using different keywords.'
                : 'Try different keywords.'}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SearchResultsPage;
